import React, { useState } from 'react';
import { X, MessageSquare, Search } from 'lucide-react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { UserAvatar } from '../common/UserAvatar';

interface NewDmModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NewDmModal: React.FC<NewDmModalProps> = ({ isOpen, onClose }) => {
  const { members } = useWorkspace();
  const { createDm } = useChat();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const otherMembers = members.filter(m => m.user.id !== user?.id);
  const filtered = otherMembers.filter(m =>
    m.user.displayName.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelectMember = async (memberId: string) => {
    try {
      setIsSubmitting(true);
      await createDm(memberId);
      onClose();
    } catch (err) {
      console.error('Failed to create direct conversation', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-base text-foreground">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <MessageSquare className="h-4 w-4" />
            </div>
            <span>New Direct Message</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted text-muted-foreground transition">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search members by name..."
              className="w-full h-10 pl-9 pr-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
              autoFocus
            />
          </div>
        </div>

        <div className="max-h-64 overflow-y-auto p-2 divide-y divide-border/60">
          {filtered.map(m => (
            <button
              key={m.id}
              onClick={() => handleSelectMember(m.user.id)}
              disabled={isSubmitting}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/60 text-left transition"
            >
              <UserAvatar
                userId={m.user.id}
                displayName={m.user.displayName}
                avatarUrl={m.user.avatarUrl}
                size="sm"
                className="shadow-2xs flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-foreground truncate">{m.user.displayName}</p>
              </div>
            </button>
          ))}

          {filtered.length === 0 && (
            <div className="p-8 text-center text-xs text-muted-foreground">
              {otherMembers.length === 0 ? 'No other members in this workspace yet. Invite teammates to start chatting!' : 'No members found'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
